<template>
  <main>
    <section>
      <div class="flex-col justify-center items-center">
        <div class="rounded-full flex justify-center">
          <img
            ref="imgProfile"
            :src="previewFile || require('@/assets/images/user.svg')"
            alt="preview-profile"
            class="rounded-full w-72 h-72 object-center object-cover"
          />
        </div>
        <div class="flex justify-center mt-3">
          <Button
            icon="pi pi-pencil"
            class="btn-simple"
            label="Editar"
            @click="openModal()"
          />
        </div>
      </div>
    </section>

    <Dialog
      v-model:visible="visible"
      :closable="false"
      :draggable="false"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '80vw' }"
      :modal="true"
      @hide="closeModal()"
      class="dialog"
    >
      <form
        class="grid grid-cols-12 gap-5 lg:divide-x-2 relative"
        @submit.prevent="submitFile()"
      >
        <section class="col-span-12 sm:col-span-12 lg:col-span-3 xl:col-span-3">
          <aside
            class="flex flex-col justify-center items-center gap-5 sticky top-2"
          >
            <div class="rounded-full flex justify-center">
              <img
                ref="imgChoose"
                :src="previewFile || require('@/assets/images/user.svg')"
                alt="preview-profile"
                class="rounded-full w-72 h-72 object-cover"
              />
            </div>
            <h6 class="text-center">
              {{
                $t("labels.generic", {
                  pt: "Foto de perfil",
                  en: "Profile photo",
                })
              }}
            </h6>
            <div class="flex flex-col mt-3">
              <input
                id="file-input"
                ref="fileInput"
                accept="image/*"
                @change="uploadFile($event)"
                @click="inputReset($event)"
                type="file"
                hidden
              />

              <Button class="btn-outline p-button-sm">
                <label for="file-input" class="flex gap-2 cursor-pointer">
                  <v-icon name="hi-solid-pencil" />
                  <span>{{
                    $t("labels.generic", {
                      pt: "Carregar foto",
                      en: "Load file",
                    })
                  }}</span>
                </label>
              </Button>
            </div>
          </aside>
        </section>
        <section class="col-span-12 sm:col-span-12 lg:col-span-9 xl:col-span-9">
          <div class="md:ml-5">
            <h4>
              {{
                $t("labels.generic", {
                  pt: "Selecionar avatar",
                  en: "Choose avatar",
                })
              }}
            </h4>
            <p class="text-gray-600 mt-3">
              {{ $t("avatar.info") }}
            </p>
            <div class="grid grid-cols-12 gap-5 mt-5">
              <div
                v-for="(item, i) in allAvatars"
                :key="i"
                :class="`col-span-3 w-full`"
              >
                <span class="hidden">
                  <RadioButton
                    name="avatar"
                    :inputId="item.id"
                    :value="item"
                    v-model="selected"
                    @change="chooseAvatar"
                  />
                </span>
                <div
                  :class="`w-full max-h-64 overflow-hidden rounded-full flex items-center ${
                    selected?.id == item.id
                      ? 'border-4 md:border-8 border-h-secondary-700 ring-4 md:ring-8 ring-yellow-100 transition-all'
                      : ''
                  }`"
                >
                  <label :for="item.id">
                    <img
                      :src="item.media?.path"
                      :alt="`item-media`"
                      :class="`object-cover rounded-full scale-125 cursor-pointer w-full h-full`"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
      <template #footer>
        <Button
          label="Cancelar"
          class="btn-outline p-button-sm"
          @click="closeModal()"
        />
        <Button
          label="Salvar"
          class="btn-primary p-button-sm"
          type="submit"
          :loading="loading"
          @click="submitFile()"
        />
      </template>
    </Dialog>
  </main>
</template>

<script lang="ts">
import { UsersRest } from "@/services/rest/users.rest";
import { getUserFromStorage } from "@/utils/user-store.util";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import { EditUserService } from "../edit-user.service";

export default defineComponent({
  name: "image-upload",
  computed: {
    rest(): UsersRest {
      return new UsersRest();
    },
    service(): EditUserService {
      return new EditUserService();
    },
  },
  data() {
    return {
      visible: false as boolean,
      loading: false as boolean,
      allAvatars: {} as any,
      selected: null as any,
      previewFile: getUserFromStorage().photoProfile as any,
      file: {} as any,
    };
  },
  props: {
    user: {
      required: true,
      type: Object,
      default: new Object(),
    },
  },
  mounted() {
    this.getAllAvatars();
  },
  updated() {
    this.previewFile = getUserFromStorage().photoProfile;
  },
  methods: {
    submitFile(): void {
      if (this.file?.id) {
        this.updateAvatarById();
      } else {
        this.addAvatar();
      }
    },
    addAvatar(): void {
      const formData = new FormData();
      this.loading = true;
      formData.append("file", this.file);
      this.rest
        .postPhotoProfile(formData)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.updateImage(response);
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    updateAvatarById(): void {
      const formData = new FormData();
      this.loading = true;
      const strData = JSON.stringify(this.file?.id);
      const jsonApp = new Blob([strData], { type: "application/json" });
      formData.append("mediaId", jsonApp);
      this.rest
        .postPhotoProfile(formData)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.updateImage(response);
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    updateImage(response: any): void {
      this.service.stockNewUser(response);
      this.previewFile = response.photoProfile.path;
      this.closeModal();
    },
    chooseAvatar(): void {
      (this.$refs.imgChoose as any).src = this.selected.media.path;
      this.file.id = this.selected?.media?.id;
    },
    uploadFile(event: any): void {
      var { files } = event.target;
      if (files[0]) {
        let reader = new FileReader();
        reader.onload = (e) => {
          (this.$refs.imgChoose as any).src = e.target?.result;
        };
        reader.readAsDataURL(files[0]);
        this.file = files[0];
      }
    },
    getAllAvatars(): void {
      this.service.avatars.subscribe({
        next: (response) => {
          this.allAvatars = response.content;
        },
      });
      this.service.getAllAvatars();
    },
    openModal(): void {
      this.visible = true;
    },
    closeModal(): void {
      this.visible = false;
      this.selected = null;
    },
    inputReset(event: any): void {
      event.target.value = "";
    },
  },
});
</script>
